import { useTranslation } from 'react-i18next';
import { RiFunctions, RiPencilLine, RiPriceTag3Line, RiRfidFill } from '@remixicon/react';
import type { FolderDocument, SensorStatus, TableConfig, TableRow, TableStatus } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { bindingDecimals, bindingUnit, deviceListEntries, tableSensorIds } from '../bindings';
import { formatDateTime, formatNumber, NO_VALUE, withUnit, DEFAULT_TIME_ZONE } from '../live/format';
import { useSensorMetaMap, useSensorValues, type SensorReading } from '../live/LiveProvider';
import { STATUS_STYLE, statusLabelKey } from '../live/status';
import { useLocale } from '../live/values';
import { evaluateFormula, FormulaError, parseNumber } from './formula';

/** Badge Small Tinted (kit 67:10639): h20, r4, padding 2/4 (+2 label), SemiBold 12/16, colour by live status. */
export function StatusBadge({ status, className }: { status: SensorStatus; className?: string }) {
  const { t } = useTranslation('constructor');
  const s = STATUS_STYLE[status];
  return (
    <span
      className={cn('inline-flex h-20 items-center rounded-4 px-6 text-xs leading-16 font-semibold whitespace-nowrap', className)}
      style={{ backgroundColor: s.badgeBg, color: s.badgeFg }}
    >
      {t(statusLabelKey(status))}
    </span>
  );
}

/* ───────────────────────── deviceList (§6.10, 590:16599) ───────────────────────── */

/** T.r. 50 · then four equal columns (194 at 854 wide). Shared by the header and body tables so they align. */
function DeviceListCols() {
  return (
    <colgroup>
      <col className="w-50" />
      <col />
      <col />
      <col />
      <col />
    </colgroup>
  );
}

export function DeviceListTable({ document, timeZone = DEFAULT_TIME_ZONE, className }: { document: FolderDocument; timeZone?: string; className?: string }) {
  const { t } = useTranslation('constructor');
  const locale = useLocale();
  const entries = deviceListEntries(document);
  const readings = useSensorValues(entries.map((e) => e.sensorId));
  const meta = useSensorMetaMap();
  const perDevice = new Map<string, number>();
  for (const e of entries) perDevice.set(e.deviceCode, (perDevice.get(e.deviceCode) ?? 0) + 1);
  const headers = [t('table.index'), t('table.device'), t('table.measuredAt'), t('table.value'), t('table.status')];

  return (
    // frame: Dark blue/200 band with a 1 px "padding" border, r12; the body is a Primary BG r12 card inside it
    <div className={cn('flex min-h-0 flex-col overflow-hidden rounded-12 bg-dark-blue-200 p-px', className)}>
      <table className="w-full shrink-0 table-fixed border-collapse" aria-hidden>
        <DeviceListCols />
        <thead>
          <tr className="h-34">
            {headers.map((h) => (
              <th key={h} className="truncate px-12 text-left text-base leading-18 font-medium tracking-normal text-dark-blue-1000">
                {h}
              </th>
            ))}
          </tr>
        </thead>
      </table>
      <div className="min-h-0 flex-1 overflow-y-auto rounded-12 bg-panel shadow-xs">
        <table className="w-full table-fixed border-collapse">
          <DeviceListCols />
          <thead className="sr-only">
            <tr>
              {headers.map((h) => (
                <th key={h} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr>
                <td colSpan={5} className="px-16 py-24 text-center text-13 text-fg-secondary">
                  {t('table.empty')}
                </td>
              </tr>
            )}
            {entries.map((e, i) => {
              const r = readings.get(e.sensorId);
              const m = meta.get(e.sensorId);
              const value = withUnit(formatNumber(r?.value, { decimals: bindingDecimals(e.binding, m), locale }), bindingUnit(e.binding, m));
              return (
                <tr key={e.key} className={cn('h-36 border-b border-line', i % 2 === 1 && 'bg-zebra')}>
                  <td className="px-12 text-sm text-fg-nav">{i + 1}.</td>
                  <td className="truncate px-12 text-sm font-medium text-fg">
                    {e.deviceCode}
                    {(perDevice.get(e.deviceCode) ?? 0) > 1 && <span className="font-normal text-fg-secondary"> · {e.sensorName}</span>}
                  </td>
                  <td className="truncate px-12 text-sm font-medium text-fg tabular-nums">{formatDateTime(r?.ts, { timeZone })}</td>
                  <td className={cn('truncate px-12 text-sm font-medium tabular-nums', r?.status === 'offline' ? 'text-fg-tertiary' : 'text-fg')}>{value}</td>
                  <td className="px-12">
                    <StatusBadge status={r?.status ?? 'unknown'} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ───────────────────────── custom table (Jadval konstruktori §7) ───────────────────────── */

type Cell = { kind: 'text'; text: string } | { kind: 'value'; value: number | null; text: string; status?: TableStatus } | { kind: 'status'; status: TableStatus | null } | { kind: 'error' };

const ROW_ICON = { manual: RiPencilLine, device: RiRfidFill, formula: RiFunctions, status: RiPriceTag3Line } as const;

/** First status whose [min, max) contains the value. */
export function matchStatus(statuses: readonly TableStatus[], value: number | null): TableStatus | null {
  if (value == null) return null;
  return statuses.find((s) => (s.min == null || value >= s.min) && (s.max == null || value < s.max)) ?? null;
}

function evaluateTable(config: TableConfig, readings: ReadonlyMap<string, SensorReading>, decimalsOf: (row: TableRow, col: number) => number | null, locale: string) {
  const rows = config.rows;
  const cols = config.columnCount;
  const cache = new Map<string, number | null>();
  const visiting = new Set<string>();

  const numeric = (rowIndex: number, col: number): number | null => {
    const key = `${rowIndex}:${col}`;
    if (cache.has(key)) return cache.get(key)!;
    if (visiting.has(key)) throw new FormulaError('cycle');
    const row = rows[rowIndex];
    if (!row) return null;
    visiting.add(key);
    let v: number | null = null;
    try {
      if (row.type === 'manual') v = parseNumber(row.cells[col]);
      else if (row.type === 'device') {
        const b = row.cells[col];
        v = b ? (readings.get(b.sensorId)?.value ?? null) : null;
      } else if (row.type === 'formula') {
        const src = row.cells[col] ?? row.rowFormula;
        v = src ? evaluateFormula(src, { column: col + 1, row: (n) => numeric(n - 1, col), x: deviceAbove(rowIndex, col) }) : null;
      }
    } finally {
      visiting.delete(key);
    }
    cache.set(key, v);
    return v;
  };

  const deviceAbove = (rowIndex: number, col: number): number | null => {
    for (let i = rowIndex - 1; i >= 0; i--) if (rows[i]?.type === 'device') return numeric(i, col);
    const first = rows.findIndex((r) => r.type === 'device');
    return first >= 0 ? numeric(first, col) : null;
  };

  return rows.map((row, ri): Cell[] =>
    Array.from({ length: cols }, (_, col): Cell => {
      try {
        switch (row.type) {
          case 'manual':
            return { kind: 'text', text: row.cells[col] ?? '' };
          case 'device': {
            const v = numeric(ri, col);
            return { kind: 'value', value: v, text: formatNumber(v, { decimals: decimalsOf(row, col), locale }), status: config.autoColor ? (matchStatus(config.statuses, v) ?? undefined) : undefined };
          }
          case 'formula': {
            const v = numeric(ri, col);
            return { kind: 'value', value: v, text: v == null ? '' : formatNumber(v, { locale }) };
          }
          case 'status': {
            let src = row.sourceRowId ? rows.findIndex((r) => r.id === row.sourceRowId) : -1;
            if (src < 0) for (let i = ri - 1; i >= 0 && src < 0; i--) if (rows[i]?.type === 'device') src = i;
            return { kind: 'status', status: src >= 0 ? matchStatus(config.statuses, numeric(src, col)) : null };
          }
        }
      } catch {
        return { kind: 'error' };
      }
    }),
  );
}

export function CustomTable({ config, className }: { config: TableConfig; className?: string }) {
  const { t } = useTranslation('constructor');
  const locale = useLocale();
  const readings = useSensorValues(tableSensorIds(config));
  const meta = useSensorMetaMap();
  const decimalsOf = (row: TableRow, col: number) => {
    if (row.type !== 'device') return null;
    const b = row.cells[col];
    return b ? bindingDecimals(b, meta.get(b.sensorId)) : null;
  };
  const cells = evaluateTable(config, readings, decimalsOf, locale);
  const labels = Array.from({ length: config.columnCount }, (_, i) => config.columnLabels[i] || String(i + 1));
  const template = { gridTemplateColumns: `minmax(8rem, 11.5rem) repeat(${config.columnCount}, minmax(3.5rem, 1fr))` };

  return (
    <div className={cn('flex min-h-0 flex-col overflow-hidden rounded-12 bg-dark-blue-200 p-px', className)}>
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="min-w-max">
          <div className="grid h-34" style={template}>
            <span className="flex items-center truncate px-10 text-13 font-medium text-dark-blue-1000">{config.cornerLabel}</span>
            {labels.map((l, i) => (
              <span key={i} className="flex items-center justify-center truncate px-6 text-13 font-medium text-dark-blue-1000">
                {l}
              </span>
            ))}
          </div>
          <div className="overflow-hidden rounded-12 bg-panel">
            {config.rows.length === 0 && <p className="px-16 py-24 text-center text-13 text-fg-secondary">{t('table.empty')}</p>}
            {config.rows.map((row, ri) => {
              const Icon = ROW_ICON[row.type];
              return (
                <div key={row.id} className="grid min-h-36" style={template}>
                  <span className="flex min-w-0 items-center gap-6 border-r border-b border-line bg-zebra px-10 py-6">
                    <Icon className="size-14 shrink-0 text-icon" aria-hidden />
                    <span className="truncate text-xs font-semibold text-fg">{row.name}</span>
                  </span>
                  {cells[ri]!.map((cell, ci) => (
                    <span key={ci} className="flex items-center justify-center border-r border-b border-line px-6 py-4 last:border-r-0">
                      <TableCell cell={cell} errorLabel={t('table.formulaError')} />
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function TableCell({ cell, errorLabel }: { cell: Cell; errorLabel: string }) {
  switch (cell.kind) {
    case 'error':
      return <span className="text-13 font-semibold text-tone-red">{errorLabel}</span>;
    case 'text':
      return <span className="truncate text-13 text-fg-nav">{cell.text}</span>;
    case 'status':
      return cell.status ? (
        <span
          className="inline-flex h-21 items-center rounded-6 border px-5 text-11 font-semibold lowercase"
          style={{ backgroundColor: `${cell.status.color}33`, borderColor: `${cell.status.color}cc`, color: cell.status.color }}
        >
          {cell.status.name}
        </span>
      ) : (
        <span className="text-13 text-fg-tertiary">{NO_VALUE}</span>
      );
    case 'value':
      if (cell.status)
        return (
          <span
            className="inline-flex items-center rounded-6 border px-8 py-3 text-13 font-semibold text-fg tabular-nums"
            style={{ backgroundColor: `${cell.status.color}33`, borderColor: `${cell.status.color}cc` }}
          >
            {cell.text}
          </span>
        );
      return <span className={cn('text-13 tabular-nums', cell.value == null ? 'text-fg-tertiary' : 'text-fg-nav')}>{cell.text || NO_VALUE}</span>;
  }
}
